import { QUOTE_QUANTITY_MAX } from './quoteRequest'

/**
 * Lista Zapytania — koszyk sklepu, który niczego nie sprzedaje.
 *
 * Sklep katalogowy nie ma koszyka po stronie serwera (POST /rest/cart zwraca 403
 * i ma tak zostać), więc lista wybranych produktów żyje wyłącznie w przeglądarce
 * i jedzie do backendu dopiero przy wysyłce zapytania, jako tablica pozycji.
 *
 * Logika siedzi tutaj, a nie w store, bo to jedyna część, którą da się
 * przetestować bez Nuxta — i jedyna, która musi zgadzać się co do zasady
 * z walidacją backendu (QuoteRequestValidator).
 *
 * Pozycja to para (produkt, wariant): ta sama koszulka w rozmiarach S i XL to
 * dwie pozycje wyceny. `id_product_attribute = 0` oznacza produkt bez wariantu —
 * tak wyglądały wszystkie pozycje sprzed obsługi wariantów i tak je czytamy.
 */

/**
 * Ten sam limit co ITEMS_MAX w backendzie.
 *
 * Gdyby front pozwolił zebrać więcej pozycji niż backend przyjmuje, użytkownik
 * dowiedziałby się o tym dopiero po wypełnieniu całego formularza — czyli
 * w najgorszym możliwym momencie.
 */
export const QUOTE_LIST_MAX_ITEMS = 50

export const QUOTE_LIST_STORAGE_KEY = 'quote-list'

/**
 * Wersja zapisu — pozwala odrzucić stary kształt danych po zmianie struktury.
 * Warianty NIE podbiły wersji: nowe pola są opcjonalne przy odczycie, więc listy
 * zebrane przed zmianą przeżywają wdrożenie. Tak samo usunięcie techniki
 * znakowania (20.10.2026): stare zapisy ją niosą, odczyt ją pomija.
 */
const QUOTE_LIST_VERSION = 1

export interface QuoteListItem {
  id_product: number
  /** Wariant (kombinacja) wybrany na karcie produktu; 0 = produkt bez wariantu. */
  id_product_attribute: number
  quantity: number
  /** Pola poniżej służą WYŁĄCZNIE do wyświetlenia listy bez odpytywania backendu. */
  name: string
  /** SKU wybranego wariantu, gdy front go znał; inaczej SKU produktu. */
  reference: string
  /** Opis wariantu („Rozmiar: XXL, Kolor: Blekitny"); '' = bez wariantu. */
  attributes: string
  image: string | null
  link: string | null
}

/**
 * Kształt przyjmowany na wejściu. Identyfikator wolno podać jako liczbę albo
 * cyfrowy ciąg, bo dokładnie takie dane daje API — normalizacja jest po naszej
 * stronie, żeby wywołujący nie musiał o niej pamiętać.
 *
 * Wariant i jego opis są opcjonalne: wywołania sprzed obsługi wariantów
 * (produkt bez kombinacji) działają bez zmian.
 */
export interface QuoteListItemInput
  extends Omit<QuoteListItem, 'id_product' | 'id_product_attribute' | 'attributes'> {
  id_product: number | string
  id_product_attribute?: number | string | null
  attributes?: string
}

export interface QuoteListMutation {
  items: QuoteListItem[]
  error: string | null
}

export interface QuoteListOptions {
  /** Minimalny nakład sklepu (BrandConfig.quoteMinQuantity); brak = 1 szt. */
  minQuantity?: number
}

/** Pozycja do POST /rest/quote-request — same identyfikatory i nakład. */
export interface QuoteListPayloadItem {
  id_product: number
  quantity: number
  /** Tylko dla pozycji z wariantem — bez wariantu klucza nie ma, jak dotąd. */
  id_product_attribute?: number
}

export const QUOTE_LIST_MESSAGES = {
  product: 'Nie udało się rozpoznać produktu.',
  variant: 'Nie udało się rozpoznać wybranego wariantu produktu.',
  quantity: 'Podaj nakład — liczbę sztuk większą od zera.',
  limit: `Lista zapytania mieści najwyżej ${QUOTE_LIST_MAX_ITEMS} pozycji. Wyślij zebrane produkty i zacznij nową listę.`,
  minQuantity: (minimum: number) => `Minimalny nakład to ${minimum} szt.`,
} as const

/** Klucz pozycji — ten sam zapis co itemKey() w QuoteRequestValidator.php. */
export function quoteListItemKey(idProduct: number, idProductAttribute = 0): string {
  return `${idProduct}:${idProductAttribute}`
}

/**
 * Identyfikator produktu do postaci liczbowej.
 *
 * API zwraca `Product.id` jako STRING (patrz shared/types/store.ts), więc
 * przyjmowanie wyłącznie `number` odrzucało każde dodanie z karty produktu.
 * Backend akceptuje cyfrowy ciąg (ctype_digit) — front musi tak samo, a potem
 * normalizować do liczby, żeby porównania identyfikatorów przez === działały
 * niezależnie od tego, skąd pozycja przyszła.
 */
function normalizeProductId(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0 ? value : null
  }

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const parsed = Number.parseInt(value.trim(), 10)

    return parsed > 0 ? parsed : null
  }

  return null
}

/** Wariant: brak/0 = produkt bez wariantu, null = wartość niepoprawna. */
function normalizeProductAttributeId(value: unknown): number | null {
  if (value === undefined || value === null || value === '' || value === 0 || value === '0') {
    return 0
  }

  return normalizeProductId(value)
}

function normalizeQuantity(value: unknown): number | null {
  const parsed = typeof value === 'number'
    ? value
    : Number.parseInt(String(value ?? '').replace(/\s/g, ''), 10)

  if (!Number.isInteger(parsed) || parsed <= 0 || parsed > QUOTE_QUANTITY_MAX) {
    return null
  }

  return parsed
}

function normalizeMinQuantity(options: QuoteListOptions): number {
  const minimum = options.minQuantity

  return typeof minimum === 'number' && Number.isInteger(minimum) && minimum > 1 ? minimum : 1
}

function findIndex(items: QuoteListItem[], idProduct: number, idProductAttribute: number): number {
  return items.findIndex(entry => entry.id_product === idProduct && entry.id_product_attribute === idProductAttribute)
}

/**
 * Dodaje produkt (wariant) do listy.
 *
 * Wariant już obecny na liście NIE tworzy drugiej pozycji — nakłady się sumują.
 * Backend odrzuca zapytanie z powtórzonym wariantem, więc duplikat nie miałby
 * prawa wyjść poza przeglądarkę; a dla użytkownika ponowne kliknięcie znaczy
 * „dorzucam jeszcze sztuk", nie „chcę dwie osobne wyceny tego samego".
 */
export function addQuoteListItem(
  items: QuoteListItem[],
  candidate: QuoteListItemInput,
  options: QuoteListOptions = {},
): QuoteListMutation {
  const idProduct = normalizeProductId(candidate.id_product)
  if (idProduct === null) {
    return { items, error: QUOTE_LIST_MESSAGES.product }
  }

  const idProductAttribute = normalizeProductAttributeId(candidate.id_product_attribute)
  if (idProductAttribute === null) {
    return { items, error: QUOTE_LIST_MESSAGES.variant }
  }

  const quantity = normalizeQuantity(candidate.quantity)
  if (quantity === null) {
    return { items, error: QUOTE_LIST_MESSAGES.quantity }
  }

  const minimum = normalizeMinQuantity(options)
  if (quantity < minimum) {
    return { items, error: QUOTE_LIST_MESSAGES.minQuantity(minimum) }
  }

  const existingIndex = findIndex(items, idProduct, idProductAttribute)
  if (existingIndex >= 0) {
    const existing = items[existingIndex]!
    const next = items.slice()
    next[existingIndex] = {
      ...existing,
      quantity: Math.min(existing.quantity + quantity, QUOTE_QUANTITY_MAX),
    }

    // Świadomie PRZED sprawdzeniem limitu: dorzucenie sztuk do pozycji, która
    // już jest na liście, nie zwiększa liczby pozycji.
    return { items: next, error: null }
  }

  if (items.length >= QUOTE_LIST_MAX_ITEMS) {
    return { items, error: QUOTE_LIST_MESSAGES.limit }
  }

  return {
    items: [...items, {
      ...candidate,
      id_product: idProduct,
      id_product_attribute: idProductAttribute,
      quantity,
      attributes: typeof candidate.attributes === 'string' ? candidate.attributes : '',
    }],
    error: null,
  }
}

export function removeQuoteListItem(
  items: QuoteListItem[],
  idProduct: number | string,
  idProductAttribute: number | string = 0,
): QuoteListItem[] {
  const normalized = normalizeProductId(idProduct)
  const normalizedAttribute = normalizeProductAttributeId(idProductAttribute)

  return normalized === null || normalizedAttribute === null
    ? items
    : items.filter(entry => !(entry.id_product === normalized && entry.id_product_attribute === normalizedAttribute))
}

export function setQuoteListQuantity(
  items: QuoteListItem[],
  idProduct: number | string,
  quantity: unknown,
  options: QuoteListOptions & { idProductAttribute?: number | string } = {},
): QuoteListMutation {
  const normalizedId = normalizeProductId(idProduct)
  const normalizedAttribute = normalizeProductAttributeId(options.idProductAttribute)
  if (normalizedId === null || normalizedAttribute === null) {
    return { items, error: QUOTE_LIST_MESSAGES.product }
  }

  const normalized = normalizeQuantity(quantity)
  if (normalized === null) {
    return { items, error: QUOTE_LIST_MESSAGES.quantity }
  }

  const minimum = normalizeMinQuantity(options)
  if (normalized < minimum) {
    return { items, error: QUOTE_LIST_MESSAGES.minQuantity(minimum) }
  }

  return {
    items: items.map(entry => (
      entry.id_product === normalizedId && entry.id_product_attribute === normalizedAttribute
        ? { ...entry, quantity: normalized }
        : entry
    )),
    error: null,
  }
}

/**
 * Pozycje z nakładem poniżej minimum sklepu. Lista zebrana przed wprowadzeniem
 * minimum (domyślnie 1 szt.) NIE jest po cichu podbijana — klient mógł wpisać
 * mniej świadomie. Strona listy pokazuje te pozycje i wstrzymuje wysyłkę.
 */
export function quoteListBelowMinimum(items: QuoteListItem[], options: QuoteListOptions = {}): QuoteListItem[] {
  const minimum = normalizeMinQuantity(options)

  return items.filter(entry => entry.quantity < minimum)
}

export function quoteListTotalQuantity(items: QuoteListItem[]): number {
  return items.reduce((total, entry) => total + entry.quantity, 0)
}

/**
 * Kształt, którego oczekuje POST /rest/quote-request.
 *
 * Nazwa, SKU, opis wariantu i zdjęcie zostają w przeglądarce — backend czyta je
 * z bazy w chwili zapytania (po id_product_attribute), żeby wątek opisywał
 * produkt takim, jakim był naprawdę, a nie takim, jakim zapamiętała go
 * przeglądarka sprzed miesiąca.
 */
export function toQuotePayloadItems(items: QuoteListItem[]): QuoteListPayloadItem[] {
  return items.map(entry => ({
    id_product: entry.id_product,
    quantity: entry.quantity,
    ...(entry.id_product_attribute > 0 ? { id_product_attribute: entry.id_product_attribute } : {}),
  }))
}

export function serializeQuoteList(items: QuoteListItem[]): string {
  return JSON.stringify({ v: QUOTE_LIST_VERSION, items })
}

/**
 * Odczyt zapisanej listy.
 *
 * Nie ufa niczemu: zapis przeżywa wdrożenia, zmiany struktury i ręczne edycje
 * w narzędziach deweloperskich. Uszkodzony wpis ma zachować się jak pusta lista,
 * bo licznik w nagłówku renderuje się na KAŻDEJ podstronie — wyjątek tutaj
 * wywaliłby cały serwis, a nie jeden widok.
 */
export function readQuoteList(raw: string | null | undefined): QuoteListItem[] {
  if (!raw) {
    return []
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  }
  catch {
    return []
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return []
  }

  const rawItems = (parsed as { items?: unknown }).items
  if (!Array.isArray(rawItems)) {
    return []
  }

  const items: QuoteListItem[] = []
  for (const entry of rawItems) {
    if (!entry || typeof entry !== 'object') {
      continue
    }

    const record = entry as Record<string, unknown>
    const idProduct = normalizeProductId(record.id_product)
    if (idProduct === null) {
      continue
    }

    const idProductAttribute = normalizeProductAttributeId(record.id_product_attribute)
    if (idProductAttribute === null) {
      continue
    }

    const quantity = normalizeQuantity(record.quantity)
    if (quantity === null) {
      continue
    }

    if (findIndex(items, idProduct, idProductAttribute) >= 0) {
      continue
    }

    items.push({
      id_product: idProduct,
      id_product_attribute: idProductAttribute,
      quantity,
      name: typeof record.name === 'string' ? record.name : '',
      reference: typeof record.reference === 'string' ? record.reference : '',
      attributes: typeof record.attributes === 'string' ? record.attributes : '',
      image: typeof record.image === 'string' ? record.image : null,
      link: typeof record.link === 'string' ? record.link : null,
    })

    if (items.length >= QUOTE_LIST_MAX_ITEMS) {
      break
    }
  }

  return items
}
